import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  authHeaders, deleteWarehousePlan, getStructureVolumes, getWarehousePlan,
  putWarehousePlanFrame, uploadWarehousePlan,
} from "../api/rest";
import { decodeThemedPlan, type DecodedPlan } from "./planDecode";
import {
  fitFrameToContent, footprintBbox, worldToPixel, type Bbox, type MapFrame2d,
} from "./planRaster";
import { tokens } from "../styles/tokens";
import css from "./WarehousePlanCard.module.css";

/**
 * ADR 215 — upload a 2D floor plan for this warehouse, without a V2Pro walk.
 *
 * The hard part is not the upload, it is the georeference: an image carries no
 * scale or origin, and a plan that is placed wrong makes every marker on it a lie.
 * So the card pairs three numbers (real width, and where the sheet's bottom-left
 * corner sits) with a preview that draws the warehouse's OWN mapped rack
 * footprints over the plan. When the drawn racks sit on the plan's racks, the
 * placement is right — judged by eye, from data, in one screen.
 *
 * "Ajustar ao conteúdo" seeds those numbers from the structure graph's extent, so
 * the common case is one click plus a nudge rather than arithmetic on a drawing
 * scale.
 */

const PREVIEW_MAX_EDGE_PX = 620;
/** Drawing 15 000 footprints would be a smear and a stall; a stride keeps the
 *  rack ROWS legible, which is what the eye aligns on. */
const MAX_PREVIEW_FOOTPRINTS = 2200;

interface Props { siteId: string; warehouseId: string }

export function WarehousePlanCard({ siteId, warehouseId }: Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [decoded, setDecoded] = useState<DecodedPlan | null>(null);
  const [widthM, setWidthM] = useState("");
  const [originX, setOriginX] = useState("0");
  const [originY, setOriginY] = useState("0");
  const [progress, setProgress] = useState(0);
  const [decodeError, setDecodeError] = useState(false);

  const planQuery = useQuery({
    queryKey: ["warehousePlan", siteId, warehouseId],
    queryFn: () => getWarehousePlan(siteId, warehouseId),
    enabled: !!siteId && !!warehouseId,
  });
  const plan = planQuery.data ?? null;

  // Shared cache key with the /live rack-volumes layer and the coverage grid.
  const volumesQuery = useQuery({
    queryKey: ["structure-volumes", siteId, warehouseId],
    queryFn: () => getStructureVolumes(siteId, warehouseId),
    enabled: !!siteId && !!warehouseId,
  });
  const footprints = useMemo(
    () => (volumesQuery.data?.volumes ?? [])
      .map((v) => v.footprint)
      .filter((f): f is number[][] => Array.isArray(f) && f.length >= 3),
    [volumesQuery.data]);
  const contentBbox: Bbox | null = useMemo(() => footprintBbox(footprints), [footprints]);

  // Decode whichever image is current: a freshly picked file wins over the stored
  // plan, so the preview always shows what "Enviar" would publish.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let blob: Blob | null = file;
        if (!blob && plan) {
          const res = await fetch(plan.url, { headers: authHeaders(), credentials: "include" });
          blob = res.ok ? await res.blob() : null;
        }
        if (!blob) {
          if (!cancelled) { setDecoded(null); setDecodeError(false); }
          return;
        }
        const out = await decodeThemedPlan(blob, PREVIEW_MAX_EDGE_PX);
        if (cancelled) return;
        setDecoded(out);
        setDecodeError(!out);
      } catch {
        if (!cancelled) { setDecoded(null); setDecodeError(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [file, plan]);

  // Seed the form: a stored plan's own frame, else a fit to the structure extent.
  const seededFor = useRef<string | null>(null);
  const seedKey = `${file ? file.name + file.size : plan?.key ?? ""}:${decoded ? decoded.sourceWidth : 0}`;
  if (decoded && seededFor.current !== seedKey) {
    seededFor.current = seedKey;
    if (!file && plan) {
      setWidthM(String(round2(plan.resolution * plan.widthPx)));
      setOriginX(String(round2(plan.originX)));
      setOriginY(String(round2(plan.originY)));
    } else if (contentBbox) {
      const fit = fitFrameToContent(contentBbox, decoded.sourceWidth, decoded.sourceHeight);
      setWidthM(String(round2(fit.widthM)));
      setOriginX(String(round2(fit.originX)));
      setOriginY(String(round2(fit.originY)));
    }
  }

  const frame: MapFrame2d | null = useMemo(() => {
    const w = Number(widthM.replace(",", "."));
    if (!decoded || !Number.isFinite(w) || w <= 0) return null;
    return {
      widthPx: decoded.width,
      heightPx: decoded.height,
      resolution: w / decoded.width,
      originX: Number(originX.replace(",", ".")) || 0,
      originY: Number(originY.replace(",", ".")) || 0,
    };
  }, [decoded, widthM, originX, originY]);

  // Repaint the preview on any change of image or numbers.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !decoded) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.putImageData(decoded.image, 0, 0);
    if (!frame) return;
    const stride = Math.max(1, Math.ceil(footprints.length / MAX_PREVIEW_FOOTPRINTS));
    ctx.strokeStyle = tokens.semantic.warn;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.85;
    for (let i = 0; i < footprints.length; i += stride) {
      const poly = footprints[i];
      ctx.beginPath();
      poly.forEach((p, j) => {
        const [px, py] = worldToPixel(frame, p[0], p[1]);
        if (j === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }, [decoded, frame, footprints]);

  const parsedFrame = useMemo(() => ({
    widthM: Number(widthM.replace(",", ".")),
    originX: Number(originX.replace(",", ".")) || 0,
    originY: Number(originY.replace(",", ".")) || 0,
  }), [widthM, originX, originY]);
  const frameValid = Number.isFinite(parsedFrame.widthM) && parsedFrame.widthM > 0;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["warehousePlan", siteId, warehouseId] });
  };

  const upload = useMutation({
    mutationFn: () => uploadWarehousePlan(siteId, warehouseId, file as File, parsedFrame, setProgress),
    onSuccess: () => { setFile(null); setProgress(0); invalidate(); },
  });
  const saveFrame = useMutation({
    mutationFn: () => putWarehousePlanFrame(siteId, warehouseId, parsedFrame),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => deleteWarehousePlan(siteId, warehouseId),
    onSuccess: () => { setFile(null); setDecoded(null); seededFor.current = null; invalidate(); },
  });
  const busy = upload.isPending || saveFrame.isPending || remove.isPending;
  const error = (upload.error ?? saveFrame.error ?? remove.error) as Error | undefined;

  const num = (v: number, digits = 1) =>
    v.toLocaleString(i18n.language, { maximumFractionDigits: digits });

  return (
    <div className={css.card}>
      <div className={css.head}>
        <span className={css.title}>{t("plan.title")}</span>
        {plan
          ? <span className={css.tagOk}>{t("plan.present")}</span>
          : <span className={css.tagNone}>{t("plan.absent")}</span>}
      </div>
      <p className={css.hint}>{t("plan.hint")}</p>

      <div className={css.row}>
        <label className={css.fileLabel}>
          {upload.isPending ? `${progress.toFixed(0)}%` : t(plan ? "plan.replace" : "plan.choose")}
          <input type="file" accept="image/png,image/jpeg" hidden disabled={busy}
                 onChange={(e) => {
                   const f = e.target.files?.[0];
                   if (f) setFile(f);
                   e.target.value = "";
                 }} />
        </label>
        {file && <span className={css.fileName}>{file.name}</span>}
        {plan && !file && (
          <button type="button" className={css.dangerBtn} disabled={busy}
                  onClick={() => { if (window.confirm(t("plan.confirmRemove"))) remove.mutate(); }}>
            {t("plan.remove")}
          </button>
        )}
      </div>

      {decodeError && <div className={css.err}>{t("plan.decodeError")}</div>}

      {decoded && (
        <>
          <div className={css.fields}>
            <div className={css.field}>
              <label className={css.label} htmlFor="plan-width">{t("plan.widthM")}</label>
              <input id="plan-width" className={css.input} inputMode="decimal" value={widthM}
                     onChange={(e) => setWidthM(e.target.value)} />
            </div>
            <div className={css.field}>
              <label className={css.label} htmlFor="plan-origin-x">{t("plan.originX")}</label>
              <input id="plan-origin-x" className={css.input} inputMode="decimal" value={originX}
                     onChange={(e) => setOriginX(e.target.value)} />
            </div>
            <div className={css.field}>
              <label className={css.label} htmlFor="plan-origin-y">{t("plan.originY")}</label>
              <input id="plan-origin-y" className={css.input} inputMode="decimal" value={originY}
                     onChange={(e) => setOriginY(e.target.value)} />
            </div>
            <button type="button" className={css.ghostBtn} disabled={!contentBbox}
                    onClick={() => {
                      if (!contentBbox) return;
                      const fit = fitFrameToContent(contentBbox, decoded.sourceWidth, decoded.sourceHeight);
                      setWidthM(String(round2(fit.widthM)));
                      setOriginX(String(round2(fit.originX)));
                      setOriginY(String(round2(fit.originY)));
                    }}>
              {t("plan.fitToContent")}
            </button>
          </div>

          <div className={css.meta}>
            {t("plan.meta", {
              w: decoded.sourceWidth, h: decoded.sourceHeight,
              // Metres per ORIGINAL pixel — what the server stores; the preview's
              // own downscale must not leak into the number shown.
              res: frameValid ? num(parsedFrame.widthM / decoded.sourceWidth, 4) : "—",
              heightM: frame ? num(frame.heightPx * frame.resolution) : "—",
            })}
          </div>

          <div className={css.previewWrap}>
            <canvas ref={canvasRef} width={decoded.width} height={decoded.height} className={css.preview} />
          </div>
          <div className={css.legend}>
            {footprints.length > 0
              ? t("plan.legend", { count: Math.min(footprints.length, MAX_PREVIEW_FOOTPRINTS) })
              : t("plan.legendNoStructure")}
          </div>

          <div className={css.actions}>
            {file ? (
              <button type="button" className={css.primaryBtn} disabled={busy || !frameValid}
                      onClick={() => upload.mutate()}>
                {upload.isPending ? t("plan.uploading") : t("plan.send")}
              </button>
            ) : (
              <button type="button" className={css.primaryBtn} disabled={busy || !frameValid}
                      onClick={() => saveFrame.mutate()}>
                {saveFrame.isPending ? t("common.saving") : t("plan.saveFrame")}
              </button>
            )}
            {(upload.isSuccess || saveFrame.isSuccess) && !busy && (
              <span className={css.okMsg}>{t("plan.saved")}</span>
            )}
          </div>
        </>
      )}

      {error && <div className={css.err}>{error.message}</div>}
    </div>
  );
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
